// Spec-rule advisories for extracted rebar line items (86d3hk6pg). The system ingests the
// grade/weldable the client actually requested (no silent auto-promotion — that was removed
// from the backend GradePromotionRules); these rules are surfaced as interactive warning
// badges the coordinator applies OPTIONALLY. Pure + module-agnostic: shared by the Sales
// Order and Quotation workspaces, which each own how the returned `patch` is applied
// (updateDraftFields+rematch vs updateLineItem auto-rematch) and any price-level handling.

// Minimal spec shape both SalesOrderLineItemDto and QuotationLineItemDto satisfy.
export interface SpecLine {
  diameterMm: number | null;
  gradeCode: string | null;
  isWeldable: boolean;
}

export type SpecAdvisoryKey = 'g60-weldable' | 'large-bar-g60';

// The rule metadata shared by the aggregate toolbar badge and the per-row status chip.
export interface SpecRule {
  key: SpecAdvisoryKey;
  /** Toolbar-badge label, e.g. "G60 → weldable". */
  label: string;
  /** Compact per-row chip label, e.g. "→ weldable". */
  shortLabel: string;
  /** Tooltip / aria description of what applying the fix does. */
  description: string;
  /** Recommended spec-only correction to merge into each violating line. */
  patch: Partial<SpecLine>;
}

export interface SpecAdvisory extends SpecRule {
  /** Number of lines currently violating this rule. */
  count: number;
  /** Keys (clientId for SO, id for Quotation) of the violating lines. */
  ids: string[];
}

// Diameters at/above this are big sizes (16mm and up — owner, 2026-09-28, 14ykpqtr22u) and are
// expected to be Grade 60. Same threshold as BIG_DIAMETER_MIN_MM in quotations/utils/pricing.ts.
const LARGE_BAR_MM = 16;

interface RuleDef extends SpecRule {
  violates: (line: SpecLine) => boolean;
}

// Rules are mutually exclusive per line (rule 1 needs grade≠60, rule 2 needs grade=60), so a
// line matches at most one. `large-bar-g60` sets weldable too (G60 ⇒ weldable), so applying
// it never leaves behind a fresh `g60-weldable` violation on the same line.
const RULES: RuleDef[] = [
  {
    key: 'large-bar-g60',
    label: '≥16mm → G60',
    shortLabel: '→ G60',
    description: `Bars ${LARGE_BAR_MM}mm and larger should be Grade 60. Apply to set this to G60 (weldable) and re-match.`,
    violates: (l) => l.diameterMm != null && l.diameterMm >= LARGE_BAR_MM && l.gradeCode !== '60',
    patch: { gradeCode: '60', isWeldable: true },
  },
  {
    key: 'g60-weldable',
    label: 'G60 → weldable',
    shortLabel: '→ weldable',
    description: 'Grade 60 rebar should be weldable. Apply to mark this G60 line weldable and re-match.',
    violates: (l) => l.gradeCode === '60' && !l.isWeldable,
    patch: { isWeldable: true },
  },
];

/** The single rule a line violates (rules are mutually exclusive), or null when it's clean. */
export function advisoryForLine(line: SpecLine): SpecRule | null {
  const rule = RULES.find((r) => r.violates(line));
  if (!rule) return null;
  const { key, label, shortLabel, description, patch } = rule;
  return { key, label, shortLabel, description, patch };
}

/**
 * Compute the active spec advisories for a set of lines. Returns only rules with at least one
 * violating line, each carrying the affected keys and the recommended spec-only patch.
 *
 * @param lines the line items to scan
 * @param keyOf maps a line to its stable key (SO: `l => l.clientId`, Quotation: `l => l.id`)
 */
export function computeSpecAdvisories<T extends SpecLine>(
  lines: readonly T[],
  keyOf: (line: T) => string,
): SpecAdvisory[] {
  const advisories: SpecAdvisory[] = [];
  for (const { violates, ...meta } of RULES) {
    const ids: string[] = [];
    for (const line of lines) {
      if (violates(line)) ids.push(keyOf(line));
    }
    if (ids.length > 0) {
      advisories.push({ ...meta, count: ids.length, ids });
    }
  }
  return advisories;
}
